import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { setBcProtegeConsultingId } from '@portal/actions/onboarding';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import { ConsultingResponse, ConsultingStatus } from '@portal/enum/consulting';
import { TaxIdentifierType } from '@portal/enum/onboardingType';
import { useNavigation } from '@portal/hooks/useNavigation';
import {
  BcProtegeDisableInstructionStep,
  BcProtegeDisableInstructions as BcProtegeInstructionsModel,
} from '@portal/models/bcProtege';
import OnboardingApi from '@portal/repositories/onboarding';
import OnboardingMsApi from '@portal/repositories/onboardingMs';
import { translate } from '@portal/services/i18n';

import './BcProtegeInstructions.less';
import MessageService from '@portal/services/message';

const POLL_INTERVAL = 10000;

const FALLBACK_STEPS: BcProtegeDisableInstructionStep[] = [
  {
    title: translate('PAGES.BC_PROTEGE.INSTRUCTIONS.SLIDE_1_TITLE'),
    body: translate('PAGES.BC_PROTEGE.INSTRUCTIONS.SLIDE_1_BODY'),
    imageUrl: '',
  },
  {
    title: translate('PAGES.BC_PROTEGE.INSTRUCTIONS.SLIDE_2_TITLE'),
    body: translate('PAGES.BC_PROTEGE.INSTRUCTIONS.SLIDE_2_BODY'),
    imageUrl: '',
  },
  {
    title: translate('PAGES.BC_PROTEGE.INSTRUCTIONS.SLIDE_3_TITLE'),
    body: translate('PAGES.BC_PROTEGE.INSTRUCTIONS.SLIDE_3_BODY'),
    imageUrl: '',
  },
];

const FALLBACK_BC_PROTEGE_URL = 'https://meubc.bcb.gov.br/meubc/';

type LocationState = {
  taxIdentifier?: string;
  accountType?: TaxIdentifierType;
  consultationId?: string;
};

const BcProtegeInstructions: React.FC = () => {
  const navigate = useNavigation<LocationState>();
  const dispatch = useDispatch();
  const taxIdentifier = navigate.location.state?.taxIdentifier;
  const accountType = navigate.location.state?.accountType;
  const consultationIdFromState = navigate.location.state?.consultationId;

  const [remoteInstructions, setRemoteInstructions] = useState<
    BcProtegeInstructionsModel | undefined
  >();
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const trackRef = useRef<HTMLOListElement | null>(null);
  const consultationIdRef = useRef<string | undefined>(consultationIdFromState);
  const cancelledRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    OnboardingApi.onboardingSettings()
      .then(
        (settings: {
          bcProtegeDisableInstructions?: BcProtegeInstructionsModel;
        }) => {
          if (cancelled) return;
          setRemoteInstructions(settings?.bcProtegeDisableInstructions);
        }
      )
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const steps = remoteInstructions?.steps?.length
    ? remoteInstructions.steps
    : FALLBACK_STEPS;
  const bcProtegeUrl =
    remoteInstructions?.bcProtegeSiteUrl ?? FALLBACK_BC_PROTEGE_URL;

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const ensureConsultation = async (): Promise<string | undefined> => {
    if (consultationIdRef.current) return consultationIdRef.current;
    if (!taxIdentifier) return undefined;
    try {
      const consultation = await OnboardingMsApi.sendConsulting(taxIdentifier);
      consultationIdRef.current = consultation.id;
      dispatch(setBcProtegeConsultingId(consultation.id));
      return consultation.id;
    } catch {
      return undefined;
    }
  };

  const checkStatusOnce = async (id: string) => {
    if (cancelledRef.current) return;
    try {
      const result = await OnboardingMsApi.getConsulting(id);
      if (cancelledRef.current) return;

      if (result.status === ConsultingStatus.ERROR) {
        stopPolling();
        navigate(getRouteStackPath('AUTH', 'BC_PROTEGE_ERROR'), {
          replace: true,
          state: { taxIdentifier, accountType },
        });
        return;
      }

      if (result.status !== ConsultingStatus.COMPLETED) return;

      if (result.response === ConsultingResponse.POSITIVE) {
        stopPolling();
        navigate(getRouteStackPath('AUTH', 'OPEN_ACCOUNT'), {
          replace: true,
          state: { taxIdentifier, accountType },
        });
        return;
      }

      if (result.response === ConsultingResponse.NEGATIVE) {
        consultationIdRef.current = undefined;
      }
    } catch (error) {
      if (error instanceof Error) {
        MessageService.error(error.message);
      }
    }
  };

  const handleAccess = async () => {
    window.open(bcProtegeUrl, '_blank', 'noopener,noreferrer');
    setWaiting(true);

    const id = await ensureConsultation();
    if (cancelledRef.current || !id) return;
    if (pollIntervalRef.current) return;

    pollIntervalRef.current = setInterval(
      () => checkStatusOnce(id),
      POLL_INTERVAL
    );
  };

  const handleExitConfirm = () => {
    stopPolling();
    setExitModalVisible(false);
    navigate('/', { replace: true });
  };

  const handleTrackScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const slideWidth = track.clientWidth;
    if (slideWidth === 0) return;
    const next = Math.round(track.scrollLeft / slideWidth);
    if (next !== activeSlide) setActiveSlide(next);
  };

  const goToSlide = (target: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(target, steps.length - 1));
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const handleTrackKey = (event: React.KeyboardEvent<HTMLOListElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goToSlide(activeSlide + 1);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goToSlide(activeSlide - 1);
    }
  };

  return (
    <div className="bc-protege-instructions-page">
      <div className="bc-protege-instructions">
        <div className="bc-protege-instructions__header">
          <ThemedText
            variant="h2"
            fontWeight="bold"
            className="bc-protege-instructions__title"
          >
            {translate('PAGES.BC_PROTEGE.INSTRUCTIONS.TITLE')}
          </ThemedText>
          <ThemedText
            variant="body"
            color="gray"
            className="bc-protege-instructions__body"
          >
            {translate('PAGES.BC_PROTEGE.INSTRUCTIONS.BODY')}
          </ThemedText>
        </div>

        <div
          className="bc-protege-instructions__carousel"
          aria-roledescription="carousel"
        >
          <ol
            ref={trackRef}
            className="bc-protege-instructions__steps"
            onScroll={handleTrackScroll}
            onKeyDown={handleTrackKey}
            tabIndex={0}
          >
            {steps.map((step, index) => (
              <li
                key={step.title}
                className="bc-protege-instructions__step"
                aria-roledescription="slide"
                aria-label={translate(
                  'PAGES.BC_PROTEGE.INSTRUCTIONS.STEP_LABEL',
                  {
                    current: index + 1,
                    total: steps.length,
                  }
                )}
              >
                {step.imageUrl ? (
                  <div className="bc-protege-instructions__step-media">
                    <img
                      src={step.imageUrl}
                      alt=""
                      loading="lazy"
                      className="bc-protege-instructions__step-image"
                    />
                  </div>
                ) : null}
                <div className="bc-protege-instructions__step-copy">
                  <ThemedText
                    variant="h3"
                    fontWeight="bold"
                    className="bc-protege-instructions__step-title"
                  >
                    {step.title}
                  </ThemedText>
                  {step.body ? (
                    <ThemedText
                      variant="body"
                      color="gray"
                      className="bc-protege-instructions__step-body"
                    >
                      {step.body}
                    </ThemedText>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>

          {steps.length > 1 ? (
            <div className="bc-protege-instructions__controls">
              <button
                type="button"
                onClick={() => goToSlide(activeSlide - 1)}
                disabled={activeSlide === 0}
                aria-label={translate(
                  'PAGES.BC_PROTEGE.INSTRUCTIONS.CAROUSEL_PREVIOUS'
                )}
                className="bc-protege-instructions__nav-button"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>
              <div
                className="bc-protege-instructions__dots"
                role="tablist"
                aria-label={translate(
                  'PAGES.BC_PROTEGE.INSTRUCTIONS.CAROUSEL_PAGINATION'
                )}
              >
                {steps.map((step, index) => (
                  <button
                    key={step.title}
                    type="button"
                    role="tab"
                    aria-selected={activeSlide === index}
                    aria-label={`${index + 1}`}
                    onClick={() => goToSlide(index)}
                    className={
                      activeSlide === index
                        ? 'bc-protege-instructions__dot bc-protege-instructions__dot--active'
                        : 'bc-protege-instructions__dot'
                    }
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => goToSlide(activeSlide + 1)}
                disabled={activeSlide === steps.length - 1}
                aria-label={translate(
                  'PAGES.BC_PROTEGE.INSTRUCTIONS.CAROUSEL_NEXT'
                )}
                className="bc-protege-instructions__nav-button"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          ) : null}
        </div>

        {waiting ? (
          <div
            className="bc-protege-instructions__waiting"
            role="status"
            aria-live="polite"
          >
            <span
              className="bc-protege-instructions__waiting-spinner"
              aria-hidden="true"
            />
            <div className="bc-protege-instructions__waiting-content">
              <ThemedText
                variant="body"
                fontWeight="semiBold"
                className="bc-protege-instructions__waiting-title"
              >
                {translate('PAGES.BC_PROTEGE.INSTRUCTIONS.WAITING_TITLE')}
              </ThemedText>
              <ThemedText
                variant="bodySmall"
                color="gray"
                className="bc-protege-instructions__waiting-text"
              >
                {translate('PAGES.BC_PROTEGE.INSTRUCTIONS.WAITING_TEXT')}
              </ThemedText>
            </div>
          </div>
        ) : null}

        <div className="bc-protege-instructions__actions">
          <AdvancedButton
            text={translate('PAGES.BC_PROTEGE.INSTRUCTIONS.ACCESS_BUTTON')}
            onClick={handleAccess}
            fullWidth
            testId="bc-protege-instructions-access"
          />
          <AdvancedButton
            variant="outlined"
            text={translate('PAGES.BC_PROTEGE.INSTRUCTIONS.EXIT_BUTTON')}
            onClick={() => setExitModalVisible(true)}
            fullWidth
            testId="bc-protege-instructions-exit"
          />
        </div>

        <GenericModal
          visible={exitModalVisible}
          title={translate('PAGES.BC_PROTEGE.INSTRUCTIONS.EXIT_CONFIRM_TITLE')}
          description={translate(
            'PAGES.BC_PROTEGE.INSTRUCTIONS.EXIT_CONFIRM_TEXT'
          )}
          onClose={() => setExitModalVisible(false)}
          actions={[
            {
              label: translate(
                'PAGES.BC_PROTEGE.INSTRUCTIONS.EXIT_CONFIRM_CANCEL'
              ),
              onClick: () => setExitModalVisible(false),
              variant: 'outlined',
            },
            {
              label: translate('PAGES.BC_PROTEGE.INSTRUCTIONS.EXIT_BUTTON'),
              onClick: handleExitConfirm,
              variant: 'primary',
            },
          ]}
        />
      </div>
    </div>
  );
};

export default BcProtegeInstructions;
